import { uuid } from '../../types/common-helper';
import {
  GameDocument,
  MediaOverview,
  Overview
} from '../../types/game-document';
import { MapEntity, ResourceEntity } from '../../types/game-document/';
import {
  DesignerSettings,
  InGameSettings,
  PreGameSettings
} from '../../types/game-document/settings';
import { AddResourceEntityAsync, GetResourcesByIds } from './resources';
import { AddMapEntityAsync } from './assets';
import cloneDeep from 'lodash.clonedeep';
import {
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
  DEFAULT_ZOOM_LEVEL
} from '../../constants/coordinate';
import {
  DefaultAllCorrectFeedbackMessageResource,
  DefaultAllWrongFeedbackMessageResource,
  DefaultPartialCorrectFeedbackMessageResource
} from './settings';

/**
 * Creates a Valid Default Game Document.
 */
export const GenerateGameDocument = async () => {
  // Initialise a new blank game document.
  let newGameDocument = GetBlankGameDocument();

  // Create the game overview resources.
  // prettier-ignore
  let overviewResources: ResourceEntity[] = [
    { id: newGameDocument.overview!.titleResId!, name: 'Title', description: 'The game\'s title', value: '', type: 'text' },
    { id: newGameDocument.overview!.shortDescriptionResId!, name: 'Short description', description: 'The game\'s short description', value: '', type: 'text' },
    { id: newGameDocument.overview!.longDescriptionResId!, name: 'Long description', description: 'The game\'s long description', value: '', type: 'text-html' },
  ];

  // Generate the blank resources for the overviews.
  for (const res of overviewResources) {
    newGameDocument = await AddResourceEntityAsync(newGameDocument, res);
  }

  // Create a new blank map.
  // TODO: Default to current users location GPS map.
  let worldMap: MapEntity = GenerateDefaultWorldMap(
    newGameDocument.rules.worldMap.mapAssId
  );

  // Add the new blank map.
  newGameDocument = await AddMapEntityAsync(newGameDocument, worldMap);

  const designerSettingResource: ResourceEntity[] = [
    {
      id: uuid(),
      name: 'Designer Setting - default available task icon',
      description: "The game's default available task icon",
      value: 'https://cdn.catalystglobal.games/resources/map-task.png',
      type: 'image',
      category: 'Task icons'
    },
    {
      id: uuid(),
      name: 'Designer Setting - default completed task icon',
      description: "The game's default completed task icon",
      value:
        'https://cdn.catalystglobal.games/resources/map-task--complete.png',
      type: 'image',
      category: 'Task icons'
    },
    {
      id: uuid(),
      name: 'Designer Setting - default feedback all correct icon',
      description: "The game's default completed feedback all correct icon",
      value:
        'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/checked.png',
      type: 'image',
      category: 'Task Feedback icons'
    },
    {
      id: uuid(),
      name: 'Designer Setting - default feedback all wrong icon',
      description: "The game's default completed feedback all wrong icon",
      value:
        'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/cross.png',
      type: 'image',
      category: 'Task Feedback icons'
    },
    {
      id: uuid(),
      name: 'Designer Setting - default feedback partially correct icon',
      description:
        "The game's default completed feedback partially correct icon",
      value:
        'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/checked.png',
      type: 'image',
      category: 'Task Feedback icons'
    }
  ];

  for (const res of designerSettingResource) {
    newGameDocument = await AddResourceEntityAsync(newGameDocument, res);
  }

  const defaultAllCorrectFeedbackMessageResId =
    await DefaultAllCorrectFeedbackMessageResource(
      newGameDocument,
      'Congratulations, you got it!'
    );
  const defaultPartialCorrectFeedbackMessageResId =
    await DefaultPartialCorrectFeedbackMessageResource(
      newGameDocument,
      'Well done, you are partially correct.'
    );
  const defaultAllWrongFeedbackMessageResId =
    await DefaultAllWrongFeedbackMessageResource(
      newGameDocument,
      `That's not the answer!`
    );

  newGameDocument.settings.designer.defaultTaskAvailableIconResId =
    designerSettingResource[0].id;
  newGameDocument.settings.designer.defaultTaskCompletedIconResId =
    designerSettingResource[1].id;
  newGameDocument.settings.designer.defaultFeedbackAllCorrectIconResId =
    designerSettingResource[2].id;
  newGameDocument.settings.designer.defaultFeedbackAllWrongIconResId =
    designerSettingResource[3].id;
  newGameDocument.settings.designer.defaultFeedbackPartialCorrectIconResId =
    designerSettingResource[4].id;
  newGameDocument.settings.designer.shouldGivePlayerScore = true;
  newGameDocument.settings.designer.shouldGiveTeamScore = true;

  newGameDocument.settings.designer.defaultAllCorrectFeedbackMessageResId =
    defaultAllCorrectFeedbackMessageResId;
  newGameDocument.settings.designer.defaultPartialCorrectFeedbackMessageResId =
    defaultPartialCorrectFeedbackMessageResId;
  newGameDocument.settings.designer.defaultAllWrongFeedbackMessageResId =
    defaultAllWrongFeedbackMessageResId;

  return newGameDocument;
};

export const GenerateDefaultWorldMap = (
  mapAssId: string,
  latitude?: number,
  longitude?: number,
  zoomLevel?: number
): MapEntity => {
  const worldMap: MapEntity = {
    id: mapAssId,
    name: 'world-map',
    description: "The game's world map",
    type: 'googleMaps',
    latitude: latitude ?? DEFAULT_LATITUDE,
    longitude: longitude ?? DEFAULT_LONGITUDE,
    zoomLevel: zoomLevel ?? DEFAULT_ZOOM_LEVEL
  };

  return worldMap;
};

/**
 * Creates a new blank game document.
 * @remarks
 * WARNING: This is not a valid game document. You will need to generate resources!
 */
export const GetBlankGameDocument = (): GameDocument => {
  return {
    name: '',
    version: '0.0.0',
    description: '',
    keywords: [],
    homepage: '',
    license: '',
    author: '',
    assets: {
      maps: [],
      items: [],
      titles: [],
      timers: [],
      assessments: [],
      tasks: [],
      areas: [],
      zones: [],
      taskContents: [],
      routes: [],
      onDemandTaskCategories: [],
      additionalResourceCategories: []
    },
    overview: GetBlankGameDocumentOverview(),
    resources: [],
    rules: {
      worldMap: {
        events: {
          onEnterMap: {},
          onExitMap: {}
        },
        timerAssId: '',
        imageResId: '',
        mapAssId: uuid(),
        tasks: [],
        zones: [],
        timers: []
      }
    },
    settings: {
      designer: GetDefaultDesignerSettings(),
      preGame: GetDefaultPreGameSettings(),
      inGame: GetDefaultInGameSettings()
    }
  };
};

/**
 * Creates a blank Game Document Overview Section.
 * @remarks
 * You should also generate the associated resources into the game document.
 */
export const GetBlankGameDocumentOverview = (): Overview => {
  return {
    logoResId: '',
    headerResId: '',
    titleResId: uuid(),
    shortDescriptionResId: uuid(),
    longDescriptionResId: uuid(),
    languages: '',
    medias: []
  };
};

/**
 * Creates the default designer settings for the game document.
 */
export const GetDefaultDesignerSettings = (): DesignerSettings => {
  return {
    taskCompletionVisibility: false,
    defaultTaskAvailableIconResId: '',
    defaultTaskCompletedIconResId: '',
    defaultTaskTitleVisibility: false,
    defaultAreaTitleVisibility: false,
    defaultZoneTitleVisibility: false,
    defaultFeedbackAllCorrectIconResId: '',
    defaultFeedbackPartialCorrectIconResId: '',
    defaultFeedbackAllWrongIconResId: '',
    defaultAllWrongFeedbackMessageResId: '',
    defaultPartialCorrectFeedbackMessageResId: '',
    defaultAllCorrectFeedbackMessageResId: '',
    shouldGivePlayerScore: true,
    shouldGiveTeamScore: true
  };
};

/**
 * Creates the default pre game settings for the game document.
 */
export const GetDefaultPreGameSettings = (): PreGameSettings => {
  return {
    showPreGameLobby: true,
    isPreGameTeamRequired: false,
    showPreGameTeams: false,
    showPreGameCreateTeams: false,
    showPreGameJoinLeaveTeams: false,
    showPreGameInventory: false,
    showPreGameRoles: false,
    showPreGameScoreboard: false,
    showPreGameChat: false,
    showPreGameFacilitatorChat: false,
    showPreGameAllPlayerChat: false,
    showPreGameDirectChat: false,
    showPreGameTeamChat: false,
    showPreGameGroupChat: false,
    showPreGameTrade: false,
    showPreGameOnDemandTask: false,
    enablePreGameChangePlayerName: false,
    profilePreGamePlayerJoin: 'avatar'
  };
};

/**
 * Creates the default in game settings for the game document.
 */
export const GetDefaultInGameSettings = (): InGameSettings => {
  return {
    showInGameInfo: true,
    showInGameTeams: false,
    showInGameCreateTeams: false,
    showInGameJoinLeaveTeams: false,
    showInGameInventory: false,
    showInGameRoles: false,
    showInGameScoreboard: false,
    showInGamePlayerTeamScore: false,
    showInGameLeaderboard: false,
    showInGameChat: false,
    showInGameFacilitatorChat: false,
    showInGameAllPlayerChat: false,
    showInGameDirectChat: false,
    showInGameTeamChat: false,
    showInGameGroupChat: false,
    showInGameTrade: false,
    tasksRequireProximity: true,
    enablePremessageOutsideRange: true,
    showOtherPlayerOrTeamOnMap: false,
    showPlayerScoreOnMap: false,
    showPlayerDetailByClickOnMap: true,
    showInGameOnDemandTask: false,
    enableInGameChangePlayerName: false
  };
};

export const AddExistingOverviewResources = (gameDocument: GameDocument) => {
  let resources: ResourceEntity[] = [];
  for (const res of (
    Object.keys(
      gameDocument?.overview!
    ) as (keyof typeof gameDocument.overview)[]
  ).filter((key: string) => key.toString().endsWith('ResId'))) {
    if (gameDocument?.overview) {
      let field: string = gameDocument.overview[res];

      if (field) {
        let resource: ResourceEntity[] = GetResourcesByIds(gameDocument, [
          field
        ]);

        if (resource.length > 0) {
          resources.push(cloneDeep(resource[0]));
        }
      }
    }
  }

  if (
    gameDocument?.overview?.medias &&
    gameDocument.overview.medias.length > 0
  ) {
    gameDocument.overview.medias.forEach((media: MediaOverview) => {
      let resource: ResourceEntity[] = GetResourcesByIds(gameDocument, [
        media?.value
      ]);

      if (resource.length > 0) {
        resources.push(cloneDeep(resource[0]));
      }
    });
  }

  return resources;
};
